/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { MatterCourtCases } from '@/pages/CourtCases';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MATTER_KIND_LABEL } from '@kd/shared';
import { DocumentsTable } from '@/components/DocumentsTable';
import { TasksTable } from '@/components/TasksTable';
import { Timeline } from '@/components/Timeline';
import { Badge, Button, ErrorText, KV, Modal, PageHeader, Spinner, Tabs } from '@/components/ui';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtDate, fmtMoney } from '@/lib/format';
import { useDirectory } from '@/lib/hooks';
import { MatterForm } from './Matters';
import { DeadlinesTable } from '@/components/Deadlines';
import { GenerateDoc } from '@/components/GenerateDoc';
import { ProcedureForm } from './Bankruptcy';
import { TimeTab } from './Time';
import { MatterAgentTab } from '@/components/MatterAgentTab';
import { AiChat } from '@/components/AiChat';
import { CaseChat } from '@/components/CaseChat';

type Tab = 'tasks' | 'court' | 'deadlines' | 'procedure' | 'timeline' | 'documents' | 'expenses' | 'time' | 'ai' | 'chat' | 'agent' | 'team';

export function MatterCardPage() {
  const { id } = useParams<{ id: string }>();
  const { can, hasModule } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('tasks');
  const [editing, setEditing] = useState(false);
  const { data: m, isLoading, error } = useQuery({ queryKey: ['matter', id], queryFn: () => api.get(`/matters/${id}`) });
  const { data: client } = useQuery({ enabled: !!m?.clientId, queryKey: ['party', m?.clientId], queryFn: () => api.get(`/parties/${m.clientId}`) });
  const close = useMutation({ mutationFn: (s: string) => api.patch(`/matters/${id}`, { status: s }), onSuccess: () => qc.invalidateQueries({ queryKey: ['matter', id] }) });
  if (isLoading) return <Spinner />;
  if (error || !m) return <ErrorText error={error} />;
  const w = can('matter.write');
  return (
    <>
      <PageHeader title={m.title} subtitle={<span className="inline-flex items-center gap-2">№ {m.number} · {MATTER_KIND_LABEL[m.kind]} ·
        <Badge tone={m.status === 'CLOSED' ? 'slate' : 'green'}>{m.status === 'CLOSED' ? 'закрыто' : 'в работе'}</Badge>
        {m.confidential && <Badge tone="amber"><Lock size={11} className="mr-1" />конфиденциально</Badge>}</span>}
        actions={w && <>
          <Button variant="secondary" onClick={() => close.mutate(m.status === 'CLOSED' ? 'OPEN' : 'CLOSED')}>{m.status === 'CLOSED' ? 'Открыть снова' : 'Закрыть дело'}</Button>
          <GenerateDoc entity="matter" entityId={id!} />
          <Button variant="secondary" onClick={() => setEditing(true)}>Изменить</Button>
        </>} />
      <div className="card mb-5 p-4"><KV items={[
        ['Клиент', client ? <Link className="text-brand-700 hover:underline" to={`/parties/${client.id}`}>{client.name}</Link> : null],
        ['Открыто', fmtDate(m.openedAt)], ['Закрыто', m.closedAt && fmtDate(m.closedAt)], ['Описание', m.description],
        ['Команда', m.members.map((x: any) => x.fullName).join(', ')],
      ]} /></div>
      <Tabs<Tab> value={tab} onChange={setTab} tabs={[{ id: 'tasks', label: 'Задачи' },
        ...(hasModule('M12') ? [{ id: 'court' as Tab, label: 'Суд' }] : []),
        ...(hasModule('M40') ? [{ id: 'deadlines' as Tab, label: 'Сроки' }] : []),
        ...(m.kind === 'BANKRUPTCY' && hasModule('M14') && can('bankruptcy.read') ? [{ id: 'procedure' as Tab, label: 'Процедура' }] : []),
        { id: 'timeline', label: 'Лента' }, { id: 'documents', label: 'Документы' },
        { id: 'expenses', label: 'Затраты' }, ...(hasModule('M35') && can('time.write') ? [{ id: 'time' as Tab, label: 'Время' }] : []), ...(hasModule('M31') ? [{ id: 'chat' as Tab, label: 'Чат с делом' }] : []), ...(hasModule('M20') ? [{ id: 'ai' as Tab, label: 'ИИ' }] : []), ...(hasModule('M32') || hasModule('M21') ? [{ id: 'agent' as Tab, label: 'Агент и практика' }] : []), { id: 'team', label: 'Команда', count: m.members.length }]} />
      {tab === 'tasks' && <TasksTable filter={{ matterId: id }} defaults={{ matterId: id, partyId: m.clientId ?? undefined }} />}
      {tab === 'court' && <MatterCourtCases matterId={id!} clientId={m.clientId} />}
      {tab === 'deadlines' && <DeadlinesTable filter={{ matterId: id }} matterId={id} />}
      {tab === 'procedure' && <MatterProcedures matterId={id!} />}
      {tab === 'time' && <TimeTab matterId={id} />}
      {tab === 'ai' && <AiChat matterId={id} />}
      {tab === 'chat' && <CaseChat matterId={id} />}
      {tab === 'agent' && <MatterAgentTab matterId={id!} />}
      {tab === 'timeline' && <div className="card p-4"><Timeline path={`/matters/${id}/timeline`} canWrite={w} /></div>}
      {tab === 'documents' && <DocumentsTable filter={{ matterId: id, partyId: m.clientId ?? undefined }} />}
      {tab === 'expenses' && <MatterExpenses matterId={id!} />}
      {tab === 'team' && <Team m={m} canWrite={w} />}
      <Modal title="Изменение дела" open={editing} onClose={() => setEditing(false)}>
        <MatterForm matter={{ ...m, clientName: client?.name }} onSaved={() => setEditing(false)} />
      </Modal>
    </>
  );
}

function MatterExpenses({ matterId }: { matterId: string }) {
  const { data } = useQuery({ queryKey: ['expenses', { matterId }], queryFn: () => api.get(`/expenses?matterId=${matterId}&pageSize=200`) });
  return (
    <div className="card p-4 text-sm">
      <div className="mb-2">Итого по делу: <b className="tabular-nums">{fmtMoney(data?.sum ?? 0)}</b></div>
      {data?.items.map((e: any) => <div key={e.id} className="flex justify-between border-t border-slate-100 py-1.5"><span>{fmtDate(e.date)} · {e.description ?? e.category}</span><span className="tabular-nums">{fmtMoney(e.amount)}</span></div>)}
      <div className="mt-2 text-xs text-slate-500">Затраты добавляются в разделе «Затраты и расходы» с привязкой к делу.</div>
    </div>
  );
}

function Team({ m, canWrite }: { m: any; canWrite: boolean }) {
  const qc = useQueryClient();
  const { data: people } = useDirectory();
  const [sel, setSel] = useState<string[]>(m.members.map((x: any) => x.userId));
  const save = useMutation({ mutationFn: () => api.put(`/matters/${m.id}/members`, { memberIds: sel }), onSuccess: () => qc.invalidateQueries({ queryKey: ['matter', m.id] }) });
  return (
    <div className="card p-4">
      <div className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
        {(people ?? []).map((p) => (
          <label key={p.id} className="flex items-center gap-2 text-sm">
            <input type="checkbox" disabled={!canWrite || p.id === m.ownerId} checked={sel.includes(p.id)}
              onChange={(e) => setSel(e.target.checked ? [...sel, p.id] : sel.filter((x) => x !== p.id))} />
            {p.fullName}{p.id === m.ownerId && <span className="text-xs text-slate-500"> (ответственный)</span>}
          </label>
        ))}
      </div>
      <ErrorText error={save.error} />
      {canWrite && <div className="mt-3 flex justify-end"><Button onClick={() => save.mutate()} disabled={save.isPending}>Сохранить команду</Button></div>}
    </div>
  );
}

function MatterProcedures({ matterId }: { matterId: string }) {
  const { can } = useAuth();
  const [creating, setCreating] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['procedures', 'matter', matterId], queryFn: () => api.get<any[]>(`/bankruptcy/by-matter/${matterId}`) });
  const qc = useQueryClient();
  if (isLoading) return <Spinner />;
  return (
    <div className="card p-4 text-sm">
      {!data?.length && <div className="mb-2 text-slate-500">Процедура ещё не добавлена.</div>}
      {data?.map((p) => (
        <div key={p.id} className="flex items-center justify-between border-b border-slate-100 py-2">
          <span>{p.caseNumber && `Дело № ${p.caseNumber} · `}введена {fmtDate(p.introducedAt)}{p.finishedAt && `, завершена ${fmtDate(p.finishedAt)}`}</span>
          <Link className="text-brand-700 hover:underline" to={`/bankruptcy/${p.id}`}>Открыть: чек-лист, реестр, торги</Link>
        </div>
      ))}
      {can('bankruptcy.write') && <Button className="mt-3" onClick={() => setCreating(true)}>Добавить процедуру</Button>}
      <Modal title="Новая процедура" open={creating} onClose={() => setCreating(false)} wide>
        <ProcedureForm matterId={matterId} onSaved={() => { setCreating(false); qc.invalidateQueries({ queryKey: ['procedures'] }); }} />
      </Modal>
    </div>
  );
}
